import { forwardRef, useId, useState, useRef, useEffect, useMemo } from 'react';
import { Loader2, AlertTriangle, Lock, Pause, XCircle, CheckCircle2, Circle, ChevronDown, ChevronRight, Copy, RotateCcw, X, Check, Folder, Search, Plus, Upload, FileText, FolderOpen } from 'lucide-react';
import clsx from 'clsx';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

function cn(...inputs) {
  return clsx(...inputs);
}
function formatCurrency(value, currency, locale) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}
function CostMeter({
  used,
  budget,
  currency = "USD",
  locale,
  warningThreshold = 0.8,
  breakdown,
  onWarning,
  onHardStop,
  state: stateOverride,
  compact = false,
  popoverContent,
  ariaLabel,
  size = "md",
  "data-testid": testId
}) {
  const popoverId = useId();
  const [open, setOpen] = useState(false);
  const usedSafe = Math.max(0, used);
  const budgetSafe = Math.max(0, budget);
  const ratio = budgetSafe > 0 ? usedSafe / budgetSafe : 0;
  const percent = Math.min(100, Math.round(ratio * 100));
  const computed = stateOverride ?? (budgetSafe === 0 ? "ok" : usedSafe >= budgetSafe ? "hardStop" : ratio >= warningThreshold ? "warning" : "ok");
  const warnedRef = useRef(false);
  const stoppedRef = useRef(false);
  useEffect(() => {
    if (!stateOverride) {
      if (computed === "warning" && !warnedRef.current) {
        warnedRef.current = true;
        onWarning?.({ used: usedSafe, budget: budgetSafe });
      }
      if (computed === "hardStop" && !stoppedRef.current) {
        stoppedRef.current = true;
        onHardStop?.({ used: usedSafe, budget: budgetSafe });
      }
      if (computed === "ok") {
        warnedRef.current = false;
        stoppedRef.current = false;
      }
    }
  }, [computed, stateOverride, onWarning, onHardStop, usedSafe, budgetSafe]);
  const usedLabel = formatCurrency(usedSafe, currency, locale);
  const budgetLabel = formatCurrency(budgetSafe, currency, locale);
  const aria = ariaLabel ?? `\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0435\u0442\u0441\u044F ${usedLabel} \u0438\u0437 ${budgetLabel}, ${percent}%`;
  const breakdownContent = useMemo(() => {
    if (popoverContent) return popoverContent;
    if (!breakdown || breakdown.length === 0) {
      return /* @__PURE__ */ jsx("p", { className: "ds-cost-meter__empty", children: "\u041D\u0435\u0442 \u0432\u044B\u0437\u043E\u0432\u043E\u0432 \u0432 \u0442\u0435\u043A\u0443\u0449\u0435\u0439 \u0441\u0435\u0441\u0441\u0438\u0438" });
    }
    return /* @__PURE__ */ jsx("ul", { className: "ds-cost-meter__breakdown", children: breakdown.map((row) => /* @__PURE__ */ jsxs("li", { className: "ds-cost-meter__breakdown-row", children: [
      /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__breakdown-label", children: row.label }),
      row.units && /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__breakdown-units", children: row.units }),
      /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__breakdown-amount", children: formatCurrency(row.amount, currency, locale) })
    ] }, `${row.label}-${row.group ?? ""}`)) });
  }, [popoverContent, breakdown, currency, locale]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-testid": testId,
      className: cn(
        "ds-cost-meter",
        `ds-cost-meter--${size}`,
        `ds-cost-meter--${computed}`,
        compact && "ds-cost-meter--compact",
        open && "ds-cost-meter--open"
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            role: "meter",
            "aria-valuenow": Math.round(usedSafe * 100) / 100,
            "aria-valuemin": 0,
            "aria-valuemax": budgetSafe || void 0,
            "aria-label": aria,
            "aria-haspopup": "dialog",
            "aria-expanded": open,
            "aria-controls": popoverId,
            onClick: () => setOpen((o) => !o),
            onMouseEnter: () => setOpen(true),
            onMouseLeave: () => setOpen(false),
            onFocus: () => setOpen(true),
            onBlur: () => setOpen(false),
            className: "ds-cost-meter__trigger",
            children: [
              computed === "warning" && /* @__PURE__ */ jsx(AlertTriangle, { "aria-hidden": "true", className: "ds-cost-meter__icon" }),
              computed === "hardStop" && /* @__PURE__ */ jsx(Lock, { "aria-hidden": "true", className: "ds-cost-meter__icon" }),
              /* @__PURE__ */ jsxs("span", { className: "ds-cost-meter__value", children: [
                usedLabel,
                budgetSafe > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__sep", children: " / " }),
                  budgetLabel
                ] })
              ] }),
              budgetSafe > 0 && /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__bar", "aria-hidden": "true", children: /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__bar-fill", style: { width: `${percent}%` } }) }),
              !compact && budgetSafe > 0 && /* @__PURE__ */ jsxs("span", { className: "ds-cost-meter__percent", children: [
                percent,
                "%"
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs("span", { "aria-live": "polite", className: "ds-cost-meter__sr", children: [
          computed === "warning" && "\u0414\u043E\u0441\u0442\u0438\u0433\u043D\u0443\u0442 \u043F\u043E\u0440\u043E\u0433 80% \u0431\u044E\u0434\u0436\u0435\u0442\u0430",
          computed === "hardStop" && "\u0411\u044E\u0434\u0436\u0435\u0442 \u0438\u0441\u0447\u0435\u0440\u043F\u0430\u043D"
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            id: popoverId,
            role: "dialog",
            "aria-label": "\u0420\u0430\u0437\u0431\u0438\u0432\u043A\u0430 \u0440\u0430\u0441\u0445\u043E\u0434\u043E\u0432",
            className: "ds-cost-meter__popover",
            children: breakdownContent
          }
        )
      ]
    }
  );
}
function formatDuration(ms) {
  if (ms == null) return "";
  if (ms < 1e3) return `${Math.round(ms)} \u043C\u0441`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)} \u0441`;
  return `${(ms / 6e4).toFixed(1)} \u043C\u0438\u043D`;
}
var STATUS_ICON = {
  queued: Circle,
  running: Loader2,
  success: CheckCircle2,
  failed: XCircle,
  requires_approval: AlertTriangle,
  cancelled: Pause
};
function ToolCallTimeline({
  calls,
  initiallyExpandedIds = [],
  onRowClick,
  onRetry,
  onCopy,
  emptyState,
  compact = false,
  "data-testid": testId
}) {
  const [expanded, setExpanded] = useState(new Set(initiallyExpandedIds));
  if (calls.length === 0) {
    return /* @__PURE__ */ jsx("div", { "data-testid": testId, className: "ds-timeline ds-timeline--empty", children: emptyState ?? /* @__PURE__ */ jsx("p", { className: "ds-timeline__empty", children: "\u0417\u0434\u0435\u0441\u044C \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F timeline \u0432\u044B\u0437\u043E\u0432\u043E\u0432 \u0430\u0433\u0435\u043D\u0442\u0430." }) });
  }
  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
    onRowClick?.(id, next.has(id));
  };
  const copyPayload = (payload) => {
    const json = typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
    if (onCopy) {
      onCopy(json);
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(json).catch(() => {
      });
    }
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "feed",
      "aria-busy": calls.some((c) => c.status === "running") || void 0,
      "data-testid": testId,
      className: cn("ds-timeline", compact && "ds-timeline--compact"),
      children: calls.map((call) => {
        const Icon = STATUS_ICON[call.status];
        const isOpen = expanded.has(call.id);
        const isSubAgent = call.parentId != null;
        return /* @__PURE__ */ jsxs(
          "article",
          {
            role: "article",
            "aria-labelledby": `tc-${call.id}-name`,
            className: cn(
              "ds-timeline__row",
              `ds-timeline__row--${call.status}`,
              isSubAgent && "ds-timeline__row--sub"
            ),
            children: [
              /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  className: "ds-timeline__row-trigger",
                  onClick: () => toggle(call.id),
                  "aria-expanded": isOpen,
                  children: [
                    /* @__PURE__ */ jsxs("span", { className: "ds-timeline__indicator", "aria-hidden": "true", children: [
                      /* @__PURE__ */ jsx(
                        Icon,
                        {
                          className: cn(
                            "ds-timeline__icon",
                            call.status === "running" && "ds-timeline__icon--spin"
                          )
                        }
                      ),
                      /* @__PURE__ */ jsx("span", { className: "ds-timeline__connector" })
                    ] }),
                    /* @__PURE__ */ jsxs("span", { className: "ds-timeline__row-body", children: [
                      /* @__PURE__ */ jsxs("span", { className: "ds-timeline__row-head", children: [
                        /* @__PURE__ */ jsx("span", { id: `tc-${call.id}-name`, className: "ds-timeline__name", children: call.name }),
                        call.durationMs != null && call.status !== "running" && /* @__PURE__ */ jsx("span", { className: "ds-timeline__duration", children: formatDuration(call.durationMs) }),
                        call.status === "running" && /* @__PURE__ */ jsx("span", { className: "ds-timeline__duration", children: "\u0432\u044B\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F\u2026" }),
                        call.status === "queued" && /* @__PURE__ */ jsx("span", { className: "ds-timeline__duration", children: "\u0432 \u043E\u0447\u0435\u0440\u0435\u0434\u0438" }),
                        isOpen ? /* @__PURE__ */ jsx(ChevronDown, { size: 14, className: "ds-timeline__chev", "aria-hidden": "true" }) : /* @__PURE__ */ jsx(ChevronRight, { size: 14, className: "ds-timeline__chev", "aria-hidden": "true" })
                      ] }),
                      call.summary && /* @__PURE__ */ jsx("span", { className: "ds-timeline__summary", children: call.summary })
                    ] })
                  ]
                }
              ),
              isOpen && /* @__PURE__ */ jsxs("div", { className: "ds-timeline__expanded", children: [
                call.errorMessage && /* @__PURE__ */ jsx("div", { className: "ds-timeline__error", children: call.errorMessage }),
                call.input !== void 0 && /* @__PURE__ */ jsxs("details", { children: [
                  /* @__PURE__ */ jsx("summary", { children: "Input" }),
                  /* @__PURE__ */ jsx("pre", { className: "ds-timeline__pre", children: JSON.stringify(call.input, null, 2) })
                ] }),
                call.output !== void 0 && /* @__PURE__ */ jsxs("details", { open: true, children: [
                  /* @__PURE__ */ jsx("summary", { children: "Output" }),
                  /* @__PURE__ */ jsx("pre", { className: "ds-timeline__pre", children: JSON.stringify(call.output, null, 2) })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "ds-timeline__actions", children: [
                  (call.input !== void 0 || call.output !== void 0) && /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      onClick: () => copyPayload(call.output ?? call.input),
                      className: "ds-timeline__action",
                      children: [
                        /* @__PURE__ */ jsx(Copy, { size: 14, "aria-hidden": "true" }),
                        " \u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C"
                      ]
                    }
                  ),
                  call.status === "failed" && onRetry && /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      onClick: () => onRetry(call.id),
                      className: "ds-timeline__action",
                      children: [
                        /* @__PURE__ */ jsx(RotateCcw, { size: 14, "aria-hidden": "true" }),
                        " \u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C"
                      ]
                    }
                  )
                ] })
              ] })
            ]
          },
          call.id
        );
      })
    }
  );
}
var Button = forwardRef(function Button2({
  variant = "primary",
  size = "md",
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}, ref) {
  const isDisabled = disabled || loading;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type,
      className: cn(
        "ds-button",
        `ds-button--${variant}`,
        `ds-button--${size}`,
        fullWidth && "ds-button--full",
        loading && "ds-button--loading",
        className
      ),
      disabled: isDisabled,
      "aria-busy": loading || void 0,
      ...rest,
      children: [
        loading ? /* @__PURE__ */ jsx(Loader2, { className: "ds-button__spinner", "aria-hidden": "true" }) : iconLeft && /* @__PURE__ */ jsx("span", { className: "ds-button__icon", "aria-hidden": "true", children: iconLeft }),
        children && /* @__PURE__ */ jsx("span", { className: "ds-button__label", children }),
        iconRight && !loading && /* @__PURE__ */ jsx("span", { className: "ds-button__icon", "aria-hidden": "true", children: iconRight })
      ]
    }
  );
});
function formatRemaining(ms) {
  if (ms <= 0) return "\u0438\u0441\u0442\u0435\u043A\u043B\u043E";
  const total = Math.floor(ms / 1e3);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function ApprovalPrompt({
  request,
  onApprove,
  onDeny,
  mode = "modal",
  closeable = false,
  onDismiss,
  disabled = false,
  renderAffectedResources,
  labels,
  "data-testid": testId
}) {
  const [comment, setComment] = useState("");
  const [denyReason, setDenyReason] = useState("");
  const [denyMode, setDenyMode] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const dialogRef = useRef(null);
  const expiresMs = request.expiresAt ? new Date(request.expiresAt).getTime() - now : Infinity;
  const expired = expiresMs <= 0;
  useEffect(() => {
    if (!request.expiresAt) return;
    const interval = setInterval(() => setNow(Date.now()), 1e3);
    return () => clearInterval(interval);
  }, [request.expiresAt]);
  useEffect(() => {
    if (expired) onDismiss?.();
  }, [expired, onDismiss]);
  useEffect(() => {
    if (mode === "modal") {
      dialogRef.current?.focus();
    }
  }, [mode]);
  const handleApprove = async () => {
    setSubmitting("approve");
    try {
      await onApprove({ comment: comment.trim() || void 0 });
    } finally {
      setSubmitting(null);
    }
  };
  const handleDeny = async () => {
    if (!denyReason.trim()) {
      setDenyMode(true);
      return;
    }
    setSubmitting("deny");
    try {
      await onDeny({ reason: denyReason.trim() });
    } finally {
      setSubmitting(null);
    }
  };
  const titleLabel = labels?.title ?? "\u0422\u0440\u0435\u0431\u0443\u0435\u0442\u0441\u044F \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435";
  const approveLabel = labels?.approveButton ?? "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C";
  const denyLabel = labels?.denyButton ?? "\u041E\u0442\u043A\u043B\u043E\u043D\u0438\u0442\u044C";
  const body = /* @__PURE__ */ jsxs(
    "div",
    {
      ref: dialogRef,
      role: mode === "modal" ? "alertdialog" : "region",
      "aria-label": titleLabel,
      "data-testid": testId,
      tabIndex: -1,
      className: cn("ds-approval", `ds-approval--${mode}`, expired && "ds-approval--expired"),
      children: [
        /* @__PURE__ */ jsxs("header", { className: "ds-approval__header", children: [
          /* @__PURE__ */ jsxs("span", { className: "ds-approval__title", children: [
            /* @__PURE__ */ jsx(AlertTriangle, { "aria-hidden": "true", className: "ds-approval__title-icon" }),
            titleLabel
          ] }),
          request.expiresAt && /* @__PURE__ */ jsxs("span", { className: "ds-approval__timer", "aria-live": "off", children: [
            "\u0438\u0441\u0442\u0435\u043A\u0430\u0435\u0442 \u0447\u0435\u0440\u0435\u0437 ",
            formatRemaining(expiresMs)
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ds-approval__body", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u0435" }),
            /* @__PURE__ */ jsx("code", { className: "ds-approval__action", children: request.action })
          ] }),
          request.reason && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u041E\u0431\u043E\u0441\u043D\u043E\u0432\u0430\u043D\u0438\u0435" }),
            /* @__PURE__ */ jsx("p", { className: "ds-approval__reason", children: request.reason })
          ] }),
          request.scopes && request.scopes.length > 0 && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u0422\u0440\u0435\u0431\u0443\u0435\u043C\u044B\u0435 scopes" }),
            /* @__PURE__ */ jsx("ul", { className: "ds-approval__scopes", children: request.scopes.map((s) => /* @__PURE__ */ jsxs(
              "li",
              {
                className: cn("ds-approval__scope", s.elevated && "ds-approval__scope--elev"),
                children: [
                  /* @__PURE__ */ jsx("code", { children: s.name }),
                  s.elevated && /* @__PURE__ */ jsx("span", { className: "ds-approval__scope-tag", children: "elevated" })
                ]
              },
              s.name
            )) })
          ] }),
          request.affectedResources && request.affectedResources.length > 0 && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u0417\u0430\u0442\u0440\u0430\u0433\u0438\u0432\u0430\u0435\u043C\u044B\u0435 \u0440\u0435\u0441\u0443\u0440\u0441\u044B" }),
            renderAffectedResources ? renderAffectedResources(request) : /* @__PURE__ */ jsxs("ul", { className: "ds-approval__resources", children: [
              request.affectedResources.slice(0, 50).map((r, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: stable list ordering
                /* @__PURE__ */ jsx("li", { children: r }, `${r}-${i}`)
              )),
              request.affectedResources.length > 50 && /* @__PURE__ */ jsxs("li", { children: [
                "\u2026 \u0438 \u0435\u0449\u0451 ",
                request.affectedResources.length - 50
              ] })
            ] })
          ] }),
          request.estimatedCost && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u041E\u0446\u0435\u043D\u043E\u0447\u043D\u0430\u044F \u0441\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u044C" }),
            /* @__PURE__ */ jsx("span", { children: request.estimatedCost })
          ] }),
          denyMode ? /* @__PURE__ */ jsx(
            "textarea",
            {
              value: denyReason,
              onChange: (e) => setDenyReason(e.target.value),
              placeholder: labels?.reasonPlaceholder ?? "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043F\u0440\u0438\u0447\u0438\u043D\u0443 \u043E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u0438\u044F\u2026",
              className: "ds-approval__textarea",
              maxLength: 4e3,
              "aria-required": "true"
            }
          ) : /* @__PURE__ */ jsx(
            "textarea",
            {
              value: comment,
              onChange: (e) => setComment(e.target.value),
              placeholder: labels?.commentPlaceholder ?? "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439 (\u043E\u043F\u0446\u0438\u043E\u043D\u0430\u043B\u044C\u043D\u043E)\u2026",
              className: "ds-approval__textarea",
              maxLength: 4e3
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("footer", { className: "ds-approval__footer", children: [
          closeable && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onDismiss, disabled: disabled || !!submitting, children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" }),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              iconLeft: /* @__PURE__ */ jsx(X, {}),
              onClick: handleDeny,
              disabled: disabled || expired || denyMode && !denyReason.trim(),
              loading: submitting === "deny",
              children: denyLabel
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "primary",
              iconLeft: /* @__PURE__ */ jsx(Check, {}),
              onClick: handleApprove,
              disabled: disabled || expired || denyMode,
              loading: submitting === "approve",
              children: approveLabel
            }
          )
        ] })
      ]
    }
  );
  if (mode === "modal") {
    return /* @__PURE__ */ jsx("div", { className: "ds-approval__backdrop", role: "presentation", children: body });
  }
  return body;
}
function WorkspaceSwitcher({
  workspaces,
  currentWorkspaceId,
  recentWorkspaceIds = [],
  onSelect,
  onCreate,
  onManage,
  loading = false,
  onSearch,
  disabled = false,
  showArchived = false,
  "data-testid": testId
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [asyncResults, setAsyncResults] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const listId = useId();
  const current = workspaces.find((w) => w.id === currentWorkspaceId);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
      setAsyncResults(null);
    }
  }, [open]);
  useEffect(() => {
    if (!onSearch || !query) {
      setAsyncResults(null);
      return;
    }
    const t = setTimeout(() => {
      onSearch(query).then(setAsyncResults).catch(() => setAsyncResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, onSearch]);
  const filtered = useMemo(() => {
    const source = asyncResults ?? workspaces;
    const visible = showArchived ? source : source.filter((w) => !w.isArchived);
    if (!query || asyncResults) return visible;
    const q = query.toLowerCase();
    return visible.filter(
      (w) => w.name.toLowerCase().includes(q) || w.slug.toLowerCase().includes(q)
    );
  }, [asyncResults, workspaces, showArchived, query]);
  const recents = useMemo(() => {
    const set = new Set(recentWorkspaceIds);
    return workspaces.filter((w) => set.has(w.id));
  }, [workspaces, recentWorkspaceIds]);
  const handleSelect = (id) => {
    onSelect(id);
    setOpen(false);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      "data-testid": testId,
      className: cn("ds-workspace-switcher", open && "ds-workspace-switcher--open"),
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "ds-workspace-switcher__trigger",
            "aria-haspopup": "listbox",
            "aria-expanded": open,
            "aria-controls": listId,
            disabled: disabled || workspaces.length === 0,
            onClick: () => setOpen((o) => !o),
            children: [
              /* @__PURE__ */ jsx(Folder, { size: 16, "aria-hidden": "true", className: "ds-workspace-switcher__trigger-icon" }),
              /* @__PURE__ */ jsx("span", { className: "ds-workspace-switcher__trigger-name", children: current?.name ?? "Workspace \u043D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D" }),
              workspaces.length > 1 && /* @__PURE__ */ jsx(ChevronDown, { size: 14, "aria-hidden": "true", className: "ds-workspace-switcher__chevron" })
            ]
          }
        ),
        open && /* @__PURE__ */ jsxs("div", { className: "ds-workspace-switcher__panel", role: "dialog", "aria-label": "Workspaces", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-workspace-switcher__search", children: [
            /* @__PURE__ */ jsx(Search, { size: 14, "aria-hidden": "true" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                ref: inputRef,
                type: "search",
                role: "combobox",
                "aria-controls": listId,
                "aria-autocomplete": "list",
                placeholder: "\u041F\u043E\u0438\u0441\u043A workspaces\u2026",
                value: query,
                onChange: (e) => setQuery(e.target.value),
                className: "ds-workspace-switcher__search-input"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("ul", { id: listId, role: "listbox", className: "ds-workspace-switcher__list", children: [
            loading && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__skeleton" }),
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__skeleton" }),
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__skeleton" })
            ] }),
            !loading && recents.length > 0 && !query && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__group", children: "\u041D\u0435\u0434\u0430\u0432\u043D\u0438\u0435" }),
              recents.slice(0, 5).map((w) => /* @__PURE__ */ jsx(
                WorkspaceRow,
                {
                  workspace: w,
                  isCurrent: w.id === currentWorkspaceId,
                  onSelect: handleSelect
                },
                `r-${w.id}`
              )),
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__divider", "aria-hidden": "true" })
            ] }),
            !loading && filtered.length === 0 && /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__empty", children: "\u041D\u0435\u0442 workspaces, \u0443\u0434\u043E\u0432\u043B\u0435\u0442\u0432\u043E\u0440\u044F\u044E\u0449\u0438\u0445 \u0437\u0430\u043F\u0440\u043E\u0441\u0443." }),
            !loading && filtered.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs("li", { className: "ds-workspace-switcher__group", children: [
                "\u0412\u0441\u0435 workspaces (",
                filtered.length,
                ")"
              ] }),
              filtered.map((w) => /* @__PURE__ */ jsx(
                WorkspaceRow,
                {
                  workspace: w,
                  isCurrent: w.id === currentWorkspaceId,
                  onSelect: handleSelect
                },
                w.id
              ))
            ] })
          ] }),
          /* @__PURE__ */ jsxs("footer", { className: "ds-workspace-switcher__footer", children: [
            onCreate && /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                className: "ds-workspace-switcher__cta",
                onClick: () => {
                  onCreate();
                  setOpen(false);
                },
                children: [
                  /* @__PURE__ */ jsx(Plus, { size: 14, "aria-hidden": "true" }),
                  " \u0421\u043E\u0437\u0434\u0430\u0442\u044C workspace"
                ]
              }
            ),
            onManage && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "ds-workspace-switcher__cta-link",
                onClick: () => {
                  onManage();
                  setOpen(false);
                },
                children: "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C \u2192"
              }
            )
          ] })
        ] })
      ]
    }
  );
}
function WorkspaceRow({
  workspace,
  isCurrent,
  onSelect
}) {
  return /* @__PURE__ */ jsx("li", { role: "option", "aria-selected": isCurrent, children: /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: cn(
        "ds-workspace-switcher__row",
        isCurrent && "ds-workspace-switcher__row--current"
      ),
      onClick: () => !isCurrent && onSelect(workspace.id),
      disabled: isCurrent,
      children: [
        /* @__PURE__ */ jsx(Folder, { size: 14, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx("span", { className: "ds-workspace-switcher__row-name", children: workspace.name }),
        workspace.permissions?.canEdit === false && /* @__PURE__ */ jsx(
          Lock,
          {
            size: 12,
            "aria-label": "\u0422\u043E\u043B\u044C\u043A\u043E \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440",
            className: "ds-workspace-switcher__row-lock"
          }
        ),
        isCurrent && /* @__PURE__ */ jsx(Check, { size: 14, "aria-hidden": "true", className: "ds-workspace-switcher__row-check" })
      ]
    }
  ) });
}
function flatDocs(nodes) {
  const out = [];
  const walk = (n) => {
    if (n.kind === "document") out.push(n);
    n.children?.forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}
function findById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const found = findById(n.children, id);
      if (found) return found;
    }
  }
  return void 0;
}
function DocumentPicker({
  tree,
  selectedIds,
  maxSelection,
  acceptMimeTypes,
  onChange,
  onUpload,
  onAttachUrl,
  onCancel,
  onConfirm,
  mode = "modal",
  loading = false,
  "data-testid": testId
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(/* @__PURE__ */ new Set());
  const [urlValue, setUrlValue] = useState("");
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allDocs = useMemo(() => flatDocs(tree), [tree]);
  const matches = useMemo(() => {
    if (!query) return null;
    const q = query.toLowerCase();
    return allDocs.filter((d) => d.name.toLowerCase().includes(q));
  }, [allDocs, query]);
  const selectedDocs = useMemo(
    () => selectedIds.map((id) => findById(tree, id)).filter(Boolean),
    [selectedIds, tree]
  );
  const isAtMax = maxSelection != null && selectedIds.length >= maxSelection ? true : false;
  const toggle = (doc) => {
    if (!doc.canRead) return;
    const next = new Set(selectedSet);
    if (next.has(doc.id)) {
      next.delete(doc.id);
    } else {
      if (isAtMax) return;
      next.add(doc.id);
    }
    onChange(Array.from(next));
  };
  const toggleFolder = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };
  const handleUpload = async (e) => {
    if (!onUpload || !e.target.files) return;
    const added = await onUpload(e.target.files);
    if (added.length > 0) {
      onChange([...selectedIds, ...added.map((d) => d.id)]);
    }
  };
  const handleAttachUrl = async () => {
    if (!onAttachUrl || !urlValue) return;
    try {
      const node = await onAttachUrl(urlValue);
      setUrlValue("");
      onChange([...selectedIds, node.id]);
    } catch {
    }
  };
  const renderRow = (doc, depth) => {
    if (doc.kind === "folder") {
      const isOpen = expanded.has(doc.id);
      return /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "ds-doc-picker__row ds-doc-picker__row--folder",
            style: { paddingLeft: `${depth * 16 + 8}px` },
            children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "ds-doc-picker__chev",
                  onClick: () => toggleFolder(doc.id),
                  "aria-expanded": isOpen,
                  "aria-label": isOpen ? "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C" : "\u0420\u0430\u0437\u0432\u0435\u0440\u043D\u0443\u0442\u044C",
                  children: isOpen ? /* @__PURE__ */ jsx(ChevronDown, { size: 14 }) : /* @__PURE__ */ jsx(ChevronRight, { size: 14 })
                }
              ),
              isOpen ? /* @__PURE__ */ jsx(FolderOpen, { size: 14 }) : /* @__PURE__ */ jsx(Folder, { size: 14 }),
              /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__row-name", children: doc.name })
            ]
          }
        ),
        isOpen && doc.children?.map((child) => renderRow(child, depth + 1))
      ] }, doc.id);
    }
    const checked = selectedSet.has(doc.id);
    const denied = !doc.canRead;
    const wrongType = Boolean(
      acceptMimeTypes && doc.mimeType && !acceptMimeTypes.includes(doc.mimeType)
    );
    const disabled = denied || !checked && (isAtMax || wrongType);
    return /* @__PURE__ */ jsxs(
      "label",
      {
        className: cn(
          "ds-doc-picker__row",
          disabled && "ds-doc-picker__row--disabled",
          denied && "ds-doc-picker__row--denied"
        ),
        style: { paddingLeft: `${depth * 16 + 30}px` },
        title: denied ? doc.denialReason ?? "\u041D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u0430" : wrongType ? "\u041D\u0435\u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438\u0439 \u0442\u0438\u043F \u0444\u0430\u0439\u043B\u0430" : isAtMax && !checked ? `\u041C\u0430\u043A\u0441\u0438\u043C\u0443\u043C ${maxSelection}` : void 0,
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked,
              onChange: () => toggle(doc),
              disabled,
              "aria-label": `\u0412\u044B\u0431\u0440\u0430\u0442\u044C ${doc.name}`
            }
          ),
          denied ? /* @__PURE__ */ jsx(Lock, { size: 14 }) : /* @__PURE__ */ jsx(FileText, { size: 14 }),
          /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__row-name", children: doc.name }),
          doc.lastModifiedAt && /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__row-meta", children: doc.lastModifiedAt })
        ]
      },
      doc.id
    );
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: mode === "modal" ? "dialog" : "region",
      "aria-label": "\u0412\u044B\u0431\u043E\u0440 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432",
      "data-testid": testId,
      className: cn("ds-doc-picker", `ds-doc-picker--${mode}`),
      children: [
        /* @__PURE__ */ jsxs("header", { className: "ds-doc-picker__header", children: [
          /* @__PURE__ */ jsx("h3", { className: "ds-doc-picker__title", children: "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u044B" }),
          onCancel && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "ds-doc-picker__close",
              "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C",
              onClick: onCancel,
              children: /* @__PURE__ */ jsx(X, { size: 16 })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__toolbar", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__search", children: [
            /* @__PURE__ */ jsx(Search, { size: 14, "aria-hidden": "true" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "search",
                placeholder: "\u041F\u043E\u0438\u0441\u043A \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432\u2026",
                value: query,
                onChange: (e) => setQuery(e.target.value)
              }
            )
          ] }),
          onUpload && /* @__PURE__ */ jsxs("label", { className: "ds-doc-picker__upload", children: [
            /* @__PURE__ */ jsx(Upload, { size: 14, "aria-hidden": "true" }),
            "\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "file",
                multiple: true,
                accept: acceptMimeTypes?.join(","),
                onChange: handleUpload,
                hidden: true
              }
            )
          ] })
        ] }),
        selectedDocs.length > 0 && /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__chips", children: [
          /* @__PURE__ */ jsxs("span", { className: "ds-doc-picker__label", children: [
            "\u0412\u044B\u0431\u0440\u0430\u043D\u043E (",
            selectedDocs.length,
            "):"
          ] }),
          selectedDocs.map((d) => /* @__PURE__ */ jsxs("span", { className: "ds-doc-picker__chip", children: [
            /* @__PURE__ */ jsx(FileText, { size: 12, "aria-hidden": "true" }),
            d.name,
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => onChange(selectedIds.filter((id) => id !== d.id)),
                "aria-label": `\u0423\u0431\u0440\u0430\u0442\u044C ${d.name}`,
                className: "ds-doc-picker__chip-remove",
                children: /* @__PURE__ */ jsx(X, { size: 12 })
              }
            )
          ] }, d.id))
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__tree", role: "tree", children: [
          loading && /* @__PURE__ */ jsx("div", { className: "ds-doc-picker__skeleton", children: "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0434\u0435\u0440\u0435\u0432\u043E\u2026" }),
          !loading && matches != null && matches.length === 0 && /* @__PURE__ */ jsx("p", { className: "ds-doc-picker__empty", children: "\u0421\u043E\u0432\u043F\u0430\u0434\u0435\u043D\u0438\u0439 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E." }),
          !loading && matches != null && matches.length > 0 && matches.map((d) => renderRow(d, 0)),
          !loading && matches == null && tree.length === 0 && /* @__PURE__ */ jsx("p", { className: "ds-doc-picker__empty", children: "\u0412 \u044D\u0442\u043E\u043C workspace \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432." }),
          !loading && matches == null && tree.length > 0 && tree.map((d) => renderRow(d, 0))
        ] }),
        onAttachUrl && /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__url", children: [
          /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__label", children: "\u0418\u043B\u0438 \u043F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u043F\u043E URL:" }),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "url",
              value: urlValue,
              onChange: (e) => setUrlValue(e.target.value),
              placeholder: "https://\u2026"
            }
          ),
          /* @__PURE__ */ jsx(Button, { size: "sm", variant: "secondary", onClick: handleAttachUrl, disabled: !urlValue, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" })
        ] }),
        /* @__PURE__ */ jsxs("footer", { className: "ds-doc-picker__footer", children: [
          onCancel && /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: onCancel, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }),
          onConfirm && /* @__PURE__ */ jsxs(Button, { variant: "primary", onClick: () => onConfirm(selectedIds), children: [
            "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C (",
            selectedIds.length,
            ")"
          ] })
        ] })
      ]
    }
  );
}

export { ApprovalPrompt, CostMeter, DocumentPicker, ToolCallTimeline, WorkspaceSwitcher };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map